import { useEffect, useState } from "react";
import { Breadcrumbs } from "../../components/breadcrumbs/index.jsx";
import Api from "../../services/api";

const downloadBlob = (response, fallbackName) => {
  const contentDisposition = response?.headers?.["content-disposition"];
  let filename = fallbackName;

  if (contentDisposition) {
    const match = contentDisposition.match(/filename="?([^"]+)"?/);
    if (match?.[1]) {
      filename = match[1];
    }
  }

  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.parentNode.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export const ExportRecords = () => {
  const [columns, setColumns] = useState([]);
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [filters, setFilters] = useState({
    is_active: "",
    is_admin: "",
    is_candidate: "",
    is_employer: "",
  });
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState("");

  useEffect(() => {
    const fetchColumns = async () => {
      setLoading(true);
      const response = await Api.userRecordExportColumns();
      setLoading(false);

      if (response?.status === 200) {
        setColumns(response.data.data || []);
        setSelectedColumns(response.data.default || []);
      }
    };

    fetchColumns();
  }, []);

  const toggleColumn = (column) => {
    setSelectedColumns((current) =>
      current.includes(column)
        ? current.filter((item) => item !== column)
        : [...current, column]
    );
  };

  const handleExport = async (type) => {
    if (!selectedColumns.length) {
      return;
    }

    setExporting(type);
    const response = await Api.exportUserRecords({
      type,
      columns: selectedColumns,
      filters,
    });
    setExporting("");

    if (response?.status === 200) {
      downloadBlob(response, `user-records.${type === "excel" ? "xlsx" : "pdf"}`);
    }
  };

  return (
    <>
      <Breadcrumbs breadcrumbs={[{ name: "Export Records", link: "" }]} />
      <div className="mx-3 lg:mx-10">
        <div className="rounded-lg border bg-white p-5 shadow-sm">
          <div className="mb-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Export User Records</h1>
              <p className="mt-1 text-sm text-gray-500">
                Select User model columns and download all user records as Excel or PDF.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setSelectedColumns(columns)}
                className="rounded-md border border-gray-300 px-4 py-2 font-semibold text-gray-700 hover:bg-gray-100"
              >
                Select All
              </button>
              <button
                type="button"
                onClick={() => setSelectedColumns([])}
                className="rounded-md border border-gray-300 px-4 py-2 font-semibold text-gray-700 hover:bg-gray-100"
              >
                Clear
              </button>
              <button
                type="button"
                disabled={!selectedColumns.length || !!exporting}
                onClick={() => handleExport("excel")}
                className="rounded-md bg-green-600 px-4 py-2 font-semibold text-white hover:bg-green-700 disabled:opacity-60"
              >
                {exporting === "excel" ? "Downloading..." : "Download Excel"}
              </button>
              <button
                type="button"
                disabled={!selectedColumns.length || !!exporting}
                onClick={() => handleExport("pdf")}
                className="rounded-md bg-red-600 px-4 py-2 font-semibold text-white hover:bg-red-700 disabled:opacity-60"
              >
                {exporting === "pdf" ? "Downloading..." : "Download PDF"}
              </button>
            </div>
          </div>

          {loading ? (
            <div className="py-12 text-center text-gray-500">Loading columns...</div>
          ) : (
            <>
              <div className="mb-5 rounded-lg border border-gray-200 bg-gray-50 p-4">
                <h2 className="mb-3 text-lg font-bold text-gray-900">Conditions</h2>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  {[
                    { key: "is_active", label: "Active Users" },
                    { key: "is_admin", label: "Admin Users" },
                    { key: "is_candidate", label: "Candidate Users" },
                    { key: "is_employer", label: "Employer Users" },
                  ].map((filter) => (
                    <label key={filter.key} className="flex flex-col gap-1 text-sm font-semibold text-gray-700">
                      {filter.label}
                      <select
                        className="h-10 rounded-md border bg-white px-3 font-normal outline-none focus:border-orange-500"
                        value={filters[filter.key]}
                        onChange={(event) =>
                          setFilters({ ...filters, [filter.key]: event.target.value })
                        }
                      >
                        <option value="">All</option>
                        <option value="1">Yes only</option>
                        <option value="0">No only</option>
                      </select>
                    </label>
                  ))}
                </div>
              </div>

              <div className="mb-3 text-sm font-semibold text-gray-600">
                Selected {selectedColumns.length} of {columns.length} columns
              </div>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
                {columns.map((column) => (
                  <label
                    key={column}
                    className={`flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm transition ${
                      selectedColumns.includes(column)
                        ? "border-orange-500 bg-orange-50 text-orange-800"
                        : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedColumns.includes(column)}
                      onChange={() => toggleColumn(column)}
                    />
                    <span className="break-all">{column}</span>
                  </label>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};
